import { ChangeEventHandler, useCallback, useEffect, useMemo, useState } from 'react'
import { fromNumberString } from '@portal/pkg-filter-criteria/number'

// Normalize a number to a clean string for display (kill FP noise).
// Strategy: clamp to ~15 significant digits, then trim trailing zeros.
const toNormalizedString = (n: number): string => {
  if (!Number.isFinite(n)) return ''
  const rounded = Number(n.toPrecision(15))
  let s = rounded.toFixed(12) // enough to remove 1e-13 style artifacts
  s = s.replace(/\.?0+$/, '') // trim trailing zeros and lone decimal point
  return s
}

export type UseNumericProps = {
  value: string
  onChange: (value: string) => void
  onBlur: VoidFunction
  numberTransformer?: {
    storedToDisplayValue: (storedValue: number) => number
    displayValueToStoredValue: (displayValue: number) => number
  }
}

export type UseNumericReturn = {
  internalValue: string
  handleChange: ChangeEventHandler<HTMLInputElement>
  handleBlur: () => void
}

export function useNumeric({
  value,
  onChange,
  onBlur,
  numberTransformer: numberTransformerProp,
}: UseNumericProps): UseNumericReturn {
  const [internalValue, setInternalValue] = useState('')

  const numberTransformer = useMemo(
    () =>
      numberTransformerProp ?? {
        storedToDisplayValue: (num) => num,
        displayValueToStoredValue: (num) => num,
      },
    [numberTransformerProp],
  )

  useEffect(() => {
    const numberConverter = fromNumberString(value)
    if (numberConverter.isValid()) {
      const numberValue = numberTransformer.storedToDisplayValue(numberConverter.toNumber())
      setInternalValue(toNormalizedString(numberValue))
    } else {
      setInternalValue('')
    }
  }, [value, numberTransformer])

  const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback((event) => {
    setInternalValue(event.target.value)
  }, [])

  const handleBlur = useCallback(() => {
    let newValidNumberValue = ''
    const numberConverter = fromNumberString(internalValue)
    if (numberConverter.isValid()) {
      const numberValue = numberTransformer.displayValueToStoredValue(numberConverter.toNumber())
      // Store a normalized string (no FP noise, no scientific notation).
      newValidNumberValue = toNormalizedString(numberValue)
    }
    onChange(newValidNumberValue)
    // Don't set internal value directly - let useEffect handle the transformation
    // setInternalValue(newValidNumberValue)

    onBlur()
  }, [internalValue, numberTransformer, onBlur, onChange])

  return { internalValue, handleChange, handleBlur }
}
